<template>
    <section class="video-section" v-if="loading || (videos && videos.length > 0)">
        <div class="section-header">
            <h2 class="section-title">
                <span v-if="icon" class="section-icon" :style="{ background: iconColor }">
                    <vue-feather :type="icon" size="16" stroke="white"></vue-feather>
                </span>
                {{ title }}
            </h2>
            <router-link v-if="seeMoreLink && !loading && videos.length > 0" :to="seeMoreLink" class="see-more-link">
                {{ currentLanguage === 'TA' ? 'மேலும்' : 'See more' }}
                <vue-feather type="chevron-right" size="18"></vue-feather>
            </router-link>
        </div>
        <div v-if="loading" :class="`video-container col-${cols}`">
            <div v-for="n in limit" :key="'shimmer-' + n" class="video-item shimmer-card">
                <div class="shimmer-thumbnail">
                    <div class="shimmer-overlay"></div>
                </div>
                <div class="shimmer-info">
                    <div class="shimmer-avatar">
                        <div class="shimmer-overlay"></div>
                    </div>
                    <div class="shimmer-text">
                        <div class="shimmer-title">
                            <div class="shimmer-overlay"></div>
                        </div>
                        <div class="shimmer-title short">
                            <div class="shimmer-overlay"></div>
                        </div>
                        <div class="shimmer-meta">
                            <div class="shimmer-overlay"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="videos && videos.length > 0" :class="`video-container col-${cols}`">
            <div
                v-for="(video, index) in displayedVideos"
                :key="video.id"
                class="video-item"
                :style="{ animationDelay: `${index * 0.05}s` }"
            >
                <video-thumb :video="video" :user-images="userImages"></video-thumb>
            </div>
        </div>

        <!-- Load More -->
        <div v-if="!loading && showLoadMore" class="section-load-more">
            <div v-if="loadingMore" class="section-loading-more">
                <span class="section-spinner"></span>
                <span>{{ currentLanguage === 'TA' ? 'ஏற்றுகிறது…' : 'Loading…' }}</span>
            </div>
            <button v-else class="section-load-more-btn" @click="onLoadMore">
                {{ currentLanguage === 'TA' ? 'மேலும் காண்பி' : 'Load more' }}
            </button>
        </div>
    </section>
</template>

<script>
import { readSessionPages, writeSessionPages } from '../utils/sessionPagination';
import VideoThumb from './VideoThumb.vue';

export default {
    name: 'VideoSection',
    components: {
        VideoThumb
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    props: {
        title: {
            type: String,
            required: true
        },
        videos: {
            type: Array,
            default: () => []
        },
        userImages: {
            type: Array,
            default: () => []
        },
        cols: {
            type: Number,
            default: 4
        },
        limit: {
            type: Number,
            default: 8
        },
        seeMoreLink: {
            type: String,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        icon: {
            type: String,
            default: null
        },
        iconColor: {
            type: String,
            default: '#e91e63'
        },
        // Whether the parent can fetch more videos for this section from the server.
        hasMore: {
            type: Boolean,
            default: false
        },
        // Whether the parent is currently fetching the next batch.
        loadingMore: {
            type: Boolean,
            default: false
        }
    },
    emits: ['load-more'],
    data() {
        return {
            // How many videos are currently revealed; grows by `limit` per click.
            displayCount: this.limit * (this.seeMoreLink
                ? readSessionPages(`section-display:${this.seeMoreLink}`) : 1)
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        displayedVideos() {
            return this.videos.slice(0, this.displayCount);
        },
        showLoadMore() {
            // More already loaded but hidden, or more available on the server.
            return this.displayCount < this.videos.length || this.hasMore;
        }
    },
    methods: {
        onLoadMore() {
            this.displayCount += this.limit;
            if (this.seeMoreLink) {
                writeSessionPages(`section-display:${this.seeMoreLink}`, Math.ceil(this.displayCount / this.limit));
            }
            // Only ask the parent to fetch when we've exhausted what's loaded.
            if (this.displayCount >= this.videos.length && this.hasMore) {
                this.$emit('load-more');
            }
        }
    }
}
</script>

<style scoped>
.video-section {
    margin-bottom: 40px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 0 20px 12px;
    border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
}

.section-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--text-secondary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex-shrink: 0;
}

.see-more-link {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-tertiary);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 20px;
    background: transparent;
    transition: all 0.2s ease;
}

.see-more-link:hover {
    color: var(--accent-primary);
    background: var(--bg-hover);
}

.see-more-link:hover svg {
    transform: translateX(3px);
}

.see-more-link svg {
    transition: transform 0.2s ease;
}

.video-container {
    display: grid;
    gap: 20px;
    padding: 0 20px;
    align-items: start;
}

.video-container.col-4 {
    grid-template-columns: repeat(4, 1fr);
}

.video-container.col-3 {
    grid-template-columns: repeat(3, 1fr);
}

.section-load-more {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding: 0 20px;
}

.section-load-more-btn {
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
    padding: 10px 28px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.section-load-more-btn:hover {
    color: var(--accent-primary);
    border-color: var(--accent-primary);
    background: var(--bg-hover);
}

.section-loading-more {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-tertiary);
    font-size: 14px;
    padding: 10px 0;
}

.section-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-color);
    border-top-color: var(--accent-primary);
    border-radius: 50%;
    animation: section-spin 0.8s linear infinite;
}

@keyframes section-spin {
    to { transform: rotate(360deg); }
}

.video-item {
    width: 100%;
    min-width: 0;
    animation: fadeInUp 0.5s ease forwards;
    opacity: 0;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Shimmer skeleton loading */
.shimmer-card {
    opacity: 1 !important;
    animation: none !important;
}

.shimmer-thumbnail {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    background: var(--bg-tertiary);
    overflow: hidden;
    position: relative;
}

.shimmer-info {
    display: flex;
    padding: 12px 4px;
    gap: 12px;
}

.shimmer-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--bg-tertiary);
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.shimmer-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.shimmer-title {
    height: 14px;
    border-radius: 4px;
    background: var(--bg-tertiary);
    overflow: hidden;
    position: relative;
    width: 100%;
}

.shimmer-title.short {
    width: 60%;
}

.shimmer-meta {
    height: 12px;
    width: 40%;
    border-radius: 4px;
    background: var(--bg-tertiary);
    overflow: hidden;
    position: relative;
}

.shimmer-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--bg-hover, rgba(255, 255, 255, 0.08)) 50%,
        transparent 100%
    );
    animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

@media (max-width: 1200px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 992px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: 1fr;
    }

    .section-title {
        font-size: 18px;
    }
}
</style>
